import React from 'react'
import Head from 'next/head'
import { useMetadata } from '../../hooks/useMetadata'
import { Helmet } from 'react-helmet-async'


import { AppConfig } from '../../constants'
interface DynamicMetaProps {
  baseUrl: string
  entityId?: string
  entityType?: 'post' | 'service' | 'doctor' | 'appointment'
  fallback?: {
    title?: string
    description?: string
    canonical?: string
    url?: string
    image?: string
  }
}

type IMetaProps = {
  title: string
  description: string
  canonical?: string
  url?: string
  image?: string
  type?: 'website' | 'article' | 'profile'
  author?: string
  publishedTime?: string
  keywords?: string
}

const Meta = ({ 
  title, 
  description, 
  url, 
  image, 
  canonical, 
  type = 'website',
  author,
  publishedTime,
  keywords
}: IMetaProps) => {
  const fullTitle = title.includes(AppConfig.siteName) ? title : `${title} | ${AppConfig.siteName}`
  const fullUrl = url || (typeof window !== 'undefined' ? window.location.href : 'https://book.drdogood.health')
  const canonicalUrl = canonical || fullUrl
  const ogImage = image || `/cover.jpg`

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph meta tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content={AppConfig.siteName} />
      <meta property="og:locale" content={AppConfig.locale} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:width" content="1200" />}
      {ogImage && <meta property="og:image:height" content="630" />}
      {ogImage && <meta property="og:image:alt" content={title} />}
      
      {/* Article specific meta tags */}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      
      {/* Twitter Card meta tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@drdogood_health" />
      <meta name="twitter:creator" content="@drdogood_health" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      
      {/* Mobile/PWA meta tags */}
      <meta charSet="UTF-8" />
      <meta 
        name="viewport" 
        content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" 
      />
      <meta name="theme-color" content="#fff" />
      
      {/* PWA manifest and icons */}
      <link rel="manifest" href="/manifest.json" />
      <link rel="icon" href="/favicon.ico" />
      <link rel="shortcut icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/manifest-icons/icon-128x128.png" />
      <link rel="icon" sizes="32x32" href="/manifest-icons/icon-96x96.png" />
      <link rel="icon" sizes="16x16" href="/manifest-icons/icon-72x72.png" />
      
      {/* Apple specific */}
      <meta name="apple-itunes-app" content="app-id=6450893844" />
      
      {/* Additional SEO tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
    </Helmet>
  )
}

const DynamicMeta = ({ entityId, entityType, baseUrl, fallback }: DynamicMetaProps) => {
  const { metadata, loading, error } = useMetadata({ baseUrl, fallback })

  // You might want to show a loading state or handle errors differently
  if (loading) {
    return (
      <Meta
        title="Loading... | DrDoGood"
        description="Loading content..."
        canonical={fallback?.canonical || ''}
        url={fallback?.url || ''}
        image={fallback?.image}
      />
    )
  }

  return (
    <Meta
      title={metadata.title}
      description={metadata.description}
      canonical={metadata.canonical}
      url={metadata.url}
      image={metadata.image}
    />
  )
}

export { Meta, DynamicMeta }
